function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalize2D(x, z) {
  const len = Math.sqrt(x * x + z * z) || 1;
  return { x: x / len, z: z / len };
}

export function getWallDirection(wall) {
  const sx = toNumber(wall?.start?.x);
  const sz = toNumber(wall?.start?.z);
  const ex = toNumber(wall?.end?.x);
  const ez = toNumber(wall?.end?.z);

  return normalize2D(ex - sx, ez - sz);
}

export function getWallLength(wall) {
  const sx = toNumber(wall?.start?.x);
  const sz = toNumber(wall?.start?.z);
  const ex = toNumber(wall?.end?.x);
  const ez = toNumber(wall?.end?.z);

  const dx = ex - sx;
  const dz = ez - sz;

  return Math.sqrt(dx * dx + dz * dz);
}

export function getWallInward(wall) {
  const inward = String(wall?.inwardDirection || wall?.inward || "").toLowerCase();

  if (inward === "positive_x") return { x: 1, z: 0 };
  if (inward === "negative_x") return { x: -1, z: 0 };
  if (inward === "positive_z") return { x: 0, z: 1 };
  if (inward === "negative_z") return { x: 0, z: -1 };

  return { x: 0, z: 1 };
}

export function getWallRotationY(wall) {
  const d = getWallDirection(wall);

  // cabinet local X follows wall direction
  const rad = Math.atan2(-d.z, d.x);

  return Number(((rad * 180) / Math.PI).toFixed(3));
}

export function clampOffsetToWall({
  wall,
  offsetFromWallStart = 0,
  width = 600,
}) {
  const wallLength = getWallLength(wall);
  const maxOffset = Math.max(0, wallLength - toNumber(width, 600));

  return Math.max(0, Math.min(toNumber(offsetFromWallStart, 0), maxOffset));
}

export function snapValue(value, snap = 10) {
  const s = toNumber(snap, 10);
  if (s <= 0) return toNumber(value, 0);

  return Math.round(toNumber(value, 0) / s) * s;
}

export function resolveWallPlacement({
  wall,
  offsetFromWallStart = 0,
  width = 600,
  height = 720,
  depth = 560,
  elevation = 0,
  snap = 10,
  clampToWall = true,
}) {
  if (!wall) {
    return null;
  }

  const direction = getWallDirection(wall);
  const inward = getWallInward(wall);

  const finalOffset = clampToWall
    ? clampOffsetToWall({
        wall,
        offsetFromWallStart,
        width,
      })
    : toNumber(offsetFromWallStart, 0);

  const snappedOffset = snapValue(finalOffset, snap);

  const centerAlongWall = snappedOffset + toNumber(width, 600) / 2;
  const centerFromWall = toNumber(depth, 560) / 2;

  const x =
    toNumber(wall.start?.x) +
    direction.x * centerAlongWall +
    inward.x * centerFromWall;

  const z =
    toNumber(wall.start?.z) +
    direction.z * centerAlongWall +
    inward.z * centerFromWall;

  return {
    placement: {
      mode: "wall",
      wallId: wall.id,
      offsetFromWallStart: snappedOffset,
      elevation: toNumber(elevation, 0),
      snap,
    },

    transform: {
      position: {
        x: Number(x.toFixed(3)),
        y: Number((toNumber(elevation, 0) + toNumber(height, 720) / 2).toFixed(3)),
        z: Number(z.toFixed(3)),
      },
      rotation: {
        x: 0,
        y: getWallRotationY(wall),
        z: 0,
      },
    },
  };
}